import { useCallback, useMemo } from 'react'
import { Graph } from 'Common/Graph/Graph'
import { CapacityGraphProps } from './Capacity.types'
import { format } from 'date-fns'

const hovertemplate = () => {
  return `%{text}<br>` + `<extra></extra>`
}

export const CapacityGraph = ({
  xAxisData,
  promisedCapacity,
  underCapacity,
  overCapacityData,
  equalCapacityData,
  unconstrainedHoursData,
}: CapacityGraphProps) => {
  const colors = {
    underCapcityBarColor: ' #D8D8D8',
    overCapacityBarColor: '#E87722',
    scatterLineColor: '#838DC8',
    equalCapacityBarColor: '#838DC8',
    unConHoursLineColor: '#d467ac',
  }

  const weeksFormatted = useMemo(() => {
    return xAxisData?.map((dateText: string, index: number) => {
      return `${format(new Date(dateText), 'dd MMM yyy')} (Week ${index + 1})`
    })
  }, [xAxisData])

  const renderHoverText = useCallback(
    (label: string, data: number[]) => {
      return data?.map(
        (d: number, i: number) =>
          `   <b>${label}</b><br>` +
          `   <b>Week:  </b>${weeksFormatted[i]}<br>` +
          `   <b>Hours: </b>${d.toFixed(1)}<br> `
      )
    },
    [xAxisData]
  )
  const trace1 = useMemo(() => {
    return {
      x: xAxisData,
      y: promisedCapacity,
      mode: 'lines',
      type: 'scatter',
      name: 'Promised capacity',
      text: renderHoverText('Promised capacity', promisedCapacity),
      hovertemplate: hovertemplate(),
      line: {
        color: colors.scatterLineColor,
        width: 3,
      },
    }
  }, [xAxisData, promisedCapacity, colors.scatterLineColor])

  const unconstrainedHours = useMemo(() => {
    return {
      x: xAxisData,
      y: unconstrainedHoursData,
      mode: 'lines',
      type: 'scatter',
      name: 'Unconstrained hours',
      text: renderHoverText('Unconstrained hours', unconstrainedHoursData),
      hovertemplate: hovertemplate(),
      line: {
        color: colors.unConHoursLineColor,
        width: 3,
        dash: 'dashdot',
      },
    }
  }, [xAxisData, unconstrainedHoursData, colors.scatterLineColor])

  const trace2 = useMemo(() => {
    return {
      x: xAxisData,
      y: underCapacity,
      textfont: { color: 'transparent' },
      name: 'Under capacity',
      text: renderHoverText('Under capacity', underCapacity),
      hovertemplate: hovertemplate(),
      marker: {
        color: colors.underCapcityBarColor,
      },
      type: 'bar',
    }
  }, [xAxisData, underCapacity, colors.underCapcityBarColor])

  const trace3 = useMemo(() => {
    return {
      x: xAxisData,
      y: overCapacityData,
      text: renderHoverText('Over capacity', overCapacityData),
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      marker: {
        color: colors.overCapacityBarColor,
      },
      name: 'Over capacity',
      type: 'bar',
    }
  }, [xAxisData, overCapacityData, colors.overCapacityBarColor])

  const trace4 = useMemo(() => {
    return {
      x: xAxisData,
      y: equalCapacityData,
      text: renderHoverText('Equal capacity', equalCapacityData),
      hovertemplate: hovertemplate(),
      textfont: { color: 'transparent' },
      marker: {
        color: colors.equalCapacityBarColor,
      },
      name: 'Matched capacity',
      type: 'bar',
    }
  }, [xAxisData, equalCapacityData, colors.equalCapacityBarColor])

  const data = useMemo(
    () => [trace1, trace2, trace3, trace4, unconstrainedHours],
    [trace1, trace2, trace3, trace4, unconstrainedHours]
  )

  const layout = {
    barmode: 'stack',
    height: 225,
    yaxis: {
      showgrid: true,
      title: {
        text: 'Hour',
      },
    },
    xaxis: {
      ntick: 10,
      tickformat: '%d %b %y',
    },
    legend: { orientation: 'h', x: 0.1, y: -0.3 },
  }

  return <Graph data={data} layout={layout} />
}
